import type { ReactNode } from 'react';

/**
 * The dashboard's form register — one Apple form, not a page of sections.
 *
 * The Apple form field at the dashboard's own size: the sign-in's `AuthField`
 * shape (a label floating at the top-left, the value under it) scaled to the
 * rail's 14px — 44px tall, 10px radius, 14/18 value, 11/14 label. Shared by
 * the raise panel and the building wizard so a field, a segmented control, a
 * switch row and a line of help are ONE definition; a screen that needs a
 * variant of any of them changes it here, where both screens see it.
 *
 * Sizes are written in `[px]` on purpose: this project's Tailwind spacing scale
 * is not the default (`--spacing-8` is 64px), so a control transcribed from
 * Apple's pixels must say the pixels.
 */
export const FIELD =
  'relative block h-11 w-full rounded-[10px] border border-[var(--line-2)] bg-[var(--paper)]/80 pr-9 pl-3 pt-[21px] text-[14px] leading-[18px] text-[var(--ink)] outline-none focus-visible:border-[var(--focus)] focus-visible:ring-1 focus-visible:ring-[var(--focus)]';

/** The floating label's classes, for a caller that needs a real `<label>` element in the slot. */
export const FIELD_LABEL =
  'pointer-events-none absolute top-[6px] left-3 text-[11px] leading-[14px] text-[var(--ink-3)]';

export function FieldLabel({ children }: { children: ReactNode }) {
  return <span className={FIELD_LABEL}>{children}</span>;
}

/** Apple's chevron, in the field's right gutter. */
export function Chevron() {
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-[var(--ink-2)]"
      width="12"
      height="12"
      viewBox="0 0 14 14"
      fill="none"
    >
      <path
        d="M3 5l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** A native select wearing the field: the value under the floating label. */
export function FieldSelect({
  label,
  value,
  disabled = false,
  options,
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="relative block">
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`${FIELD} appearance-none focus:border-[var(--focus)] focus:ring-1 focus:ring-[var(--focus)] disabled:opacity-60`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <FieldLabel>{label}</FieldLabel>
      <Chevron />
    </label>
  );
}

/**
 * A text input wearing the field. The label span sits inside the <label>
 * after the input (the sign-in's structure), so the tests' "find the span
 * reading Instruction, take its label's input" keeps working.
 */
export function FieldInput({
  label,
  value,
  disabled = false,
  onChange,
  placeholder,
  autoFocus,
  type = 'text',
  inputMode,
  autoComplete,
  onEnter,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel';
  autoComplete?: string;
  /** Enter in the field — the one key a short add-a-thing form answers to. */
  onEnter?: () => void;
}) {
  return (
    <label className="relative block">
      <input
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoFocus={autoFocus}
        inputMode={inputMode}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={
          onEnter
            ? (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onEnter();
                }
              }
            : undefined
        }
        className={`${FIELD} pr-3 focus:border-[var(--focus)] focus:ring-1 focus:ring-[var(--focus)] disabled:opacity-60`}
      />
      <FieldLabel>{label}</FieldLabel>
    </label>
  );
}

/** A field that only states a value — nothing to change, so no chevron. */
export function FieldStatic({
  label,
  muted,
  children,
}: {
  label: string;
  muted?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`${FIELD} pr-3 ${muted ? 'text-[var(--ink-2)]' : ''}`}>
      <FieldLabel>{label}</FieldLabel>
      <span className="block truncate">{children}</span>
    </div>
  );
}

/**
 * One line of help under a field — only where it says something the label
 * does not. `live` marks a line that changes as the person acts (a corner
 * count, "on the map"), so a screen reader hears the change.
 */
export function Help({
  children,
  tone,
  live = false,
}: {
  children: ReactNode;
  tone?: 'attention';
  live?: boolean;
}) {
  return (
    <p
      aria-live={live ? 'polite' : undefined}
      className={`mt-1 mb-0 px-0.5 text-[12px] leading-[16px] ${
        tone === 'attention' ? 'text-[var(--attention)]' : 'text-[var(--ink-2)]'
      }`}
    >
      {children}
    </p>
  );
}

/**
 * The segmented control: one track, the chosen segment lifted. Real radios
 * underneath (`name`/`value` pinned by the tests), visually hidden, so the
 * keyboard and a screen reader get a radio group.
 */
export function Segmented<T extends string>({
  name,
  legend,
  value,
  options,
  disabled = false,
  onChange,
}: {
  name: string;
  legend: string;
  value: T;
  options: { value: T; label: string; glyph?: ReactNode; danger?: boolean }[];
  disabled?: boolean;
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="sr-only">{legend}</legend>
      <div className="flex gap-1 rounded-[10px] bg-[var(--paper-3)] p-1">
        {options.map((o) => {
          const active = value === o.value;
          return (
            <label
              key={o.value}
              className={`group relative flex h-[32px] flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-[7px] px-2 text-[14px] font-medium transition-colors has-focus-visible:ring-2 has-focus-visible:ring-[var(--focus)] ${
                active
                  ? `elev-1 bg-[var(--paper)] ${o.danger ? 'text-[var(--alert)]' : 'text-[var(--ink)]'}`
                  : 'text-[var(--ink-2)] hover:text-[var(--ink)]'
              } ${disabled ? 'cursor-default opacity-60' : ''}`}
            >
              <input
                type="radio"
                name={name}
                value={o.value}
                checked={active}
                disabled={disabled}
                onChange={() => onChange(o.value)}
                className="sr-only"
              />
              {o.glyph ? (
                <span aria-hidden className="shrink-0">
                  {o.glyph}
                </span>
              ) : null}
              <span className="truncate">{o.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * A row with Apple's switch on the right. The checkbox is real (`aria-label`
 * is the row's name for a reader and the tests), the switch is drawn from
 * its state.
 */
export function Switch({
  checked,
  disabled = false,
  ariaLabel,
  describedBy,
  onChange,
  children,
}: {
  checked: boolean;
  disabled?: boolean;
  ariaLabel: string;
  describedBy?: string;
  onChange: () => void;
  children: ReactNode;
}) {
  return (
    <label
      className={`group flex min-h-10 items-center gap-3 px-3 py-1.5 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
    >
      <span className="min-w-0 flex-1">{children}</span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        className="sr-only"
      />
      <span
        aria-hidden
        className="relative h-[20px] w-[34px] shrink-0 rounded-full bg-[var(--line-2)] transition-colors group-has-checked:bg-[var(--safe)] group-has-disabled:opacity-50 group-has-focus-visible:ring-2 group-has-focus-visible:ring-[var(--focus)] group-has-focus-visible:ring-offset-1"
      >
        <span className="absolute top-[2px] left-[2px] h-[16px] w-[16px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.3)] transition-[translate] group-has-checked:translate-x-[14px]" />
      </span>
    </label>
  );
}

/**
 * An inset group: a bordered column of rows under one 11px label — the raise
 * panel's points group. Rows are the caller's `<li>`s (a `Switch`, a line of
 * text, an add-row button); the group draws the hairlines between them.
 */
export function FieldGroup({
  label,
  children,
  listLabel,
}: {
  label: string;
  /** A name for the list, where the visible label alone would not say what the rows are. */
  listLabel?: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-[12px] border border-[var(--line-2)] bg-[var(--paper)]/80">
      <p className="m-0 px-3 pt-2 pb-0.5 text-[11px] leading-[14px] text-[var(--ink-3)]">{label}</p>
      <ul aria-label={listLabel} className="m-0 list-none divide-y divide-[var(--line)] p-0">
        {children}
      </ul>
    </div>
  );
}

/** A row of plain text inside a `FieldGroup` — a saved thing, with its actions on the right. */
export function GroupRow({
  children,
  detail,
  actions,
}: {
  children: ReactNode;
  detail?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex min-h-10 items-center gap-3 px-3 py-1.5">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] leading-[18px]">{children}</span>
        {detail ? (
          <span className="block truncate text-[12px] leading-[15px] text-[var(--ink-2)]">
            {detail}
          </span>
        ) : null}
      </span>
      {actions ? <span className="flex shrink-0 items-center gap-1">{actions}</span> : null}
    </div>
  );
}

/**
 * The "Add …" row at the foot of a group — iOS's grouped-list add row. A
 * button, so it greys and stays put while another thing is being added.
 */
export function AddRow({
  children,
  disabled = false,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex min-h-10 w-full items-center gap-2 px-3 py-1.5 text-left text-[14px] leading-[18px] text-[var(--accent)] hover:bg-[var(--paper-3)] disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
    >
      <span aria-hidden className="text-[16px] leading-none">
        +
      </span>
      {children}
    </button>
  );
}
